import React, { useState, useEffect, useRef } from 'react';
import styles from "./MyProfile.module.css"
import EditableField from '../../components/EditableField/EditableField';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Button from '@mui/material/Button';
import CancelButton from '../../components/CancelButton/CancelButton';
import useProfileQuery from '../../hooks/useProfileQuery';
import { useSelector } from 'react-redux';
import keycloak from '../../keycloak';
import { PDFDocument } from 'pdf-lib';

const MyProfile = () => {
    const SECURITY_ANSWER_REGEX = /^[a-zA-Z0-9]{3,25}$/;
    const ALLOWED_VOID_CHEQUE_TYPES = new Set([
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/tiff',
        'image/tif',
    ]);
    const ALLOWED_VOID_CHEQUE_EXTENSIONS = /\.(pdf|jpe?g|png|tiff?)$/i;
    const deriveTransferOption = (profile) => {
        const hasVoidCheque = Boolean((profile?.voidChequeFilename || '').trim());
        const hasInteracDetails = Boolean((profile?.interacId || '').trim() && (profile?.securityAnswer || '').trim());
        if (hasVoidCheque) return 'BANK_TRANSFER';
        if (hasInteracDetails) return 'E_TRANSFER';
        return '';
    };
    const [isEditing, setEditing] = useState(false);
    const [pageLoading, setLoading] = useState(true);
    const [isDetailsConfirmed, setIsDetailsConfirmed] = useState(false);
    const [hasChosenPaymentMethod, setHasChosenPaymentMethod] = useState(false);
    const [selectedTransferOption, setSelectedTransferOption] = useState('');
    const [voidChequePreviewUrl, setVoidChequePreviewUrl] = useState('');
    const [voidChequePreviewMimeType, setVoidChequePreviewMimeType] = useState('');
    const voidChequeInputRef = useRef(null);
    // Get email from Keycloak if authenticated
    let email = null;
    if (keycloak && keycloak.authenticated && keycloak.tokenParsed && keycloak.tokenParsed.email) {
        email = keycloak.tokenParsed.email;
        sessionStorage.setItem("email", email); // keep sessionStorage in sync
    } else {
        email = keycloak.tokenParsed?.email;
    }
    const { loading, data, updateMyProfile, refetch } = useProfileQuery(email);
    const isDarkMode = useSelector(state => state.graphql.darkMode);


    const initialFormData = {
        name: '',
        organization: '',
        phoneNumber: '',
        addressLine1: '',
        city: '',
        province: '',
        country: '',
        postalCode: '',
        interacId: '',
        securityAnswer: '',
        voidChequeFilename: '',
        voidChequeFileContent: null,
        voidChequeFileSize: null,
        voidChequeFileMimeType: '',
    };

    const [formData, setFormData] = useState(initialFormData);
    const [fetchedData, setFetchedData] = useState(initialFormData);

    const setVoidChequePreviewState = (objectUrl, mimeType) => {
        setVoidChequePreviewUrl((prevUrl) => {
            if (prevUrl && prevUrl.startsWith('blob:')) {
                URL.revokeObjectURL(prevUrl);
            }
            return objectUrl;
        });
        setVoidChequePreviewMimeType((mimeType || '').toLowerCase());
    };

    const clearVoidChequePreview = () => {
        setVoidChequePreviewState('', '');
    };

    const setVoidChequePreviewFromFile = (file) => {
        const previewUrl = URL.createObjectURL(file);
        setVoidChequePreviewState(previewUrl, file.type || '');
    };

    const setVoidChequePreviewFromStoredContent = (base64Content, mimeType) => {
        if (!base64Content) {
            clearVoidChequePreview();
            return;
        }

        try {
            const binary = atob(String(base64Content).replace(/\s/g, ''));
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: mimeType || 'application/pdf' });
            const previewUrl = URL.createObjectURL(blob);
            setVoidChequePreviewState(previewUrl, mimeType || 'application/pdf');
        } catch (error) {
            console.error('Unable to build void cheque preview from stored data:', error);
            clearVoidChequePreview();
        }
    };

    console.log("data:", data);
    useEffect(() => {
        if (data && data.getUserProfile) {
            const userProfile = data.getUserProfile.body;
            const normalizedProfile = { ...initialFormData, ...userProfile };
            setFormData(normalizedProfile);
            setLoading(false);
            setFetchedData(normalizedProfile);
            setSelectedTransferOption(deriveTransferOption(normalizedProfile));
            setVoidChequePreviewFromStoredContent(normalizedProfile.voidChequeFileContent, normalizedProfile.voidChequeFileMimeType);
        }
    }, [data]);

    useEffect(() => {
        return () => {
            setVoidChequePreviewUrl((prevUrl) => {
                if (prevUrl && prevUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(prevUrl);
                }
                return '';
            });
        };
    }, []);

    const bytesToBase64 = (bytes) => {
        let binary = '';
        const chunkSize = 0x8000;
        for (let i = 0; i < bytes.length; i += chunkSize) {
            binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
        }
        return btoa(binary);
    };

    const convertImageFileToPdfBytes = async (pdfDoc, file) => {
        const imageBytes = await file.arrayBuffer();
        const fileType = (file.type || '').toLowerCase();

        if (fileType === 'image/jpeg' || fileType === 'image/jpg') {
            const img = await pdfDoc.embedJpg(imageBytes);
            const { width, height } = img.scale(1);
            const page = pdfDoc.addPage([width, height]);
            page.drawImage(img, { x: 0, y: 0, width, height });
            return;
        }

        if (fileType === 'image/png') {
            const img = await pdfDoc.embedPng(imageBytes);
            const { width, height } = img.scale(1);
            const page = pdfDoc.addPage([width, height]);
            page.drawImage(img, { x: 0, y: 0, width, height });
            return;
        }

        // TIFF fallback: decode in browser and embed as PNG page
        if (fileType === 'image/tiff' || fileType === 'image/tif') {
            const bitmap = await createImageBitmap(file);
            const canvas = document.createElement('canvas');
            canvas.width = bitmap.width;
            canvas.height = bitmap.height;
            const ctx = canvas.getContext('2d');
            if (!ctx) throw new Error('Canvas context unavailable for TIFF conversion.');
            ctx.drawImage(bitmap, 0, 0);
            const pngDataUrl = canvas.toDataURL('image/png');
            const pngBase64 = pngDataUrl.split(',')[1] || '';
            const pngBytes = Uint8Array.from(atob(pngBase64), (c) => c.charCodeAt(0));
            const img = await pdfDoc.embedPng(pngBytes);
            const { width, height } = img.scale(1);
            const page = pdfDoc.addPage([width, height]);
            page.drawImage(img, { x: 0, y: 0, width, height });
            return;
        }

        throw new Error(`Unsupported image format: ${file.name}`);
    };

    const convertVoidChequeToPdf = async (file) => {
        const fileType = (file.type || '').toLowerCase();
        const pdfDoc = await PDFDocument.create();

        if (fileType === 'application/pdf') {
            const pdfBytes = await file.arrayBuffer();
            const externalPDFDoc = await PDFDocument.load(pdfBytes);
            const copiedPages = await pdfDoc.copyPages(externalPDFDoc, externalPDFDoc.getPageIndices());
            copiedPages.forEach((page) => pdfDoc.addPage(page));
        } else {
            await convertImageFileToPdfBytes(pdfDoc, file);
        }

        return pdfDoc.save();
    };

    const handleVoidChequeChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        const normalizedType = (file.type || '').toLowerCase();
        const hasAllowedExtension = ALLOWED_VOID_CHEQUE_EXTENSIONS.test(file.name || '');
        if (!ALLOWED_VOID_CHEQUE_TYPES.has(normalizedType) && !hasAllowedExtension) {
            alert('Unsupported file type. Please upload PDF, JPG, JPEG, PNG, or TIFF.');
            event.target.value = '';
            return;
        }

        try {
            const pdfBytes = await convertVoidChequeToPdf(file);
            const pdfByteArray = new Uint8Array(pdfBytes);
            const pdfBase64 = bytesToBase64(pdfByteArray);
            const baseName = (file.name || 'void-cheque').replace(/\.[^.]+$/, '');
            setVoidChequePreviewFromFile(file);

            setFormData((prev) => ({
                ...prev,
                voidChequeFilename: `${baseName}.pdf`,
                voidChequeFileContent: pdfBase64,
                voidChequeFileSize: pdfByteArray.length,
                voidChequeFileMimeType: 'application/pdf',
            }));
        } catch (error) {
            console.error('Void cheque conversion failed:', error);
            alert('Unable to process this file. Please upload PDF, JPG, JPEG, PNG, or TIFF.');
            event.target.value = '';
            clearVoidChequePreview();
        }
    };

    const handleRemoveVoidCheque = () => {
        setFormData((prev) => ({
            ...prev,
            voidChequeFilename: '',
            voidChequeFileContent: null,
            voidChequeFileSize: null,
            voidChequeFileMimeType: '',
        }));
        clearVoidChequePreview();
        if (voidChequeInputRef.current) {
            voidChequeInputRef.current.value = '';
        }
    };

    const handleUploadVoidChequeClick = () => {
        voidChequeInputRef.current?.click();
    };

    const handleSaveClick = async () => {
        const nextFormData = { ...formData };

        if (hasChosenPaymentMethod) {
            if (!selectedTransferOption) {
                alert('Please select a payment method.');
                return;
            }

            if (selectedTransferOption === 'E_TRANSFER') {
                const normalizedInteracId = (nextFormData.interacId || '').trim();
                if (!normalizedInteracId) {
                    alert('Please provide Interac ID.');
                    return;
                }
                const normalizedSecurityAnswer = (nextFormData.securityAnswer || '').trim();
                if (!normalizedSecurityAnswer) {
                    alert('Please provide Security Answer.');
                    return;
                }
                if (!SECURITY_ANSWER_REGEX.test(normalizedSecurityAnswer)) {
                    alert('Security Answer must be alphanumeric and 3 to 25 characters.');
                    return;
                }
                nextFormData.interacId = normalizedInteracId;
                nextFormData.securityAnswer = normalizedSecurityAnswer;
                nextFormData.voidChequeFilename = '';
                nextFormData.voidChequeFileContent = null;
                nextFormData.voidChequeFileSize = null;
                nextFormData.voidChequeFileMimeType = '';
            } else {
                if (!(nextFormData.voidChequeFilename || '').trim()) {
                    alert('Please upload a void cheque for bank transfer.');
                    return;
                }
                nextFormData.interacId = "";
                nextFormData.securityAnswer = "";
            }

            if (!isDetailsConfirmed) {
                alert('Please confirm that these details are correct before saving.');
                return;
            }
        }

        const changes = {};

        for (const field in nextFormData) {
            if (nextFormData.hasOwnProperty(field) && fetchedData.hasOwnProperty(field)) {
                //Check if field was modified
                if (nextFormData[field] !== fetchedData[field]) {
                    changes[field] = nextFormData[field];
                }
            }
        }

        //If any field was modified, update database
        if (Object.keys(changes).length !== 0) {
            const email = keycloak.tokenParsed?.email; 
            console.log(changes);

            try {
                await updateMyProfile(email, changes);
                setEditing(false);
                setHasChosenPaymentMethod(false);
                setFormData(nextFormData);
                setFetchedData(nextFormData);
                refetch();
            } catch (error) {
                console.error("Error updating profile:", error);
            }
        } else {
            setEditing(false);
            setHasChosenPaymentMethod(false);
        }
    };

    const handleEditClick = () => {
        setIsDetailsConfirmed(false);
        setHasChosenPaymentMethod(false);
        setEditing(true);
    };

    const handleCancelClick = () => {
        setFormData(fetchedData);
        setSelectedTransferOption(deriveTransferOption(fetchedData));
        setIsDetailsConfirmed(false);
        setHasChosenPaymentMethod(false);
        setEditing(false);
        setVoidChequePreviewFromStoredContent(fetchedData.voidChequeFileContent, fetchedData.voidChequeFileMimeType);
        if (voidChequeInputRef.current) {
            voidChequeInputRef.current.value = '';
        }
    };

    // Update Display
    const handleChange = (fieldname, fieldvalue) => {
        setFormData((prevData) => ({
            ...prevData,
            [fieldname]: fieldvalue,
        }))
    };

    const handleTransferOptionChange = (option) => {
        setSelectedTransferOption(option);
        setHasChosenPaymentMethod(true);
        setIsDetailsConfirmed(false);
        if (option === 'E_TRANSFER') {
            setFormData((prevData) => ({
                ...prevData,
                voidChequeFilename: '',
                voidChequeFileContent: null,
                voidChequeFileSize: null,
                voidChequeFileMimeType: '',
            }));
            clearVoidChequePreview();
            if (voidChequeInputRef.current) {
                voidChequeInputRef.current.value = '';
            }
        } else {
            setFormData((prevData) => ({
                ...prevData,
                interacId: '',
                securityAnswer: '',
            }));
        }
    };

    const theme = createTheme({
        palette: {
            primary: {
                main: '#153243',
            },
            secondary: {
                main: '#7BB863'
            }
        },
    });

    if (pageLoading) {
        return null;
    }
    const hasVoidCheque = Boolean(formData.voidChequeFilename);
    const isBankTransfer = selectedTransferOption === 'BANK_TRANSFER';
    const isETransfer = selectedTransferOption === 'E_TRANSFER';
    const isPreviewImage = (voidChequePreviewMimeType || '').startsWith('image/');
    const isPreviewPdf = (voidChequePreviewMimeType || '').includes('pdf');
    const profileFieldClassName = `${styles.compactField} ${styles.fixedWidthField}`;

    return (
        <div>
            <div className={styles.container}>
                <div className={styles.header}>
                    <h2 className={styles.title}>My Profile</h2>
                    <div className={styles.buttonContainer}>
                        <ThemeProvider theme={theme}>
                            <div className={styles.actionButtons}>
                                <Button
                                    variant="contained"
                                    color={isDarkMode ? "secondary" : "primary"}
                                    size="large"
                                    className={styles.profileSaveButton}
                                    onClick={isEditing ? handleSaveClick : handleEditClick}
                                >
                                    {isEditing ? 'Save' : 'Edit'}
                                </Button>
                                {isEditing && (
                                    <CancelButton
                                        label="Cancel"
                                        onClick={handleCancelClick}
                                        useContainer={false}
                                        className={styles.profileCancelButton}
                                    />
                                )}
                            </div>
                        </ThemeProvider>
                    </div>
                </div>

                <div className={styles.items}>
                    <table className={styles.itemTable}>
                        <tbody>
                            <tr className={styles.sectionHeaderRow}>
                                <th className={styles.sectionHeader}>Personal Details</th>
                                <th className={styles.sectionSpacer}></th>
                            </tr>

                            <tr>
                                <th className={styles.sectionInstructionCell} colSpan={2}>
                                    <div className={styles.sectionInstruction}>
                                        Name should match your banking account to receive payments.
                                    </div>
                                </th>
                            </tr>

                            <tr>
                                <th className={styles.label}>Name</th>
                                <th className={styles.control}>
                                    <EditableField
                                        isEditing={isEditing}
                                        customClassName={profileFieldClassName}
                                        onChange={(update) => handleChange('name', update)}
                                        initialData={formData.name} /></th>
                            </tr>

                                <tr>
                                    <th className={styles.label}>Organization</th>
                                    <th className={styles.control}>
                                        <EditableField
                                            isEditing={false}
                                            customClassName={profileFieldClassName}
                                            initialData={formData.organization}
                                        />
                                    </th>
                                </tr>

                                <tr>
                                    <th className={styles.label}>Phone Number</th>
                                    <th className={styles.control}>
                                        <EditableField
                                            isEditing={isEditing}
                                            customClassName={profileFieldClassName}
                                            onChange={(update) => handleChange('phoneNumber', update)}
                                            initialData={formData.phoneNumber} /></th>
                                </tr>

                                <tr>
                                    <th className={styles.label}>Address Line</th>
                                    <th className={styles.control}>
                                        <EditableField isEditing={isEditing}
                                            customClassName={profileFieldClassName}
                                            onChange={(update) => handleChange('addressLine1', update)}
                                            initialData={formData.addressLine1} /></th>
                                </tr>

                                <tr>
                                    <th className={styles.label}>City</th>
                                    <th className={styles.control}>
                                        <EditableField
                                            isEditing={isEditing}
                                            customClassName={profileFieldClassName}
                                            onChange={(update) => handleChange('city', update)}
                                            initialData={formData.city} /></th>
                                </tr>

                                <tr>
                                    <th className={styles.label}>Province</th>
                                    <th className={styles.control}>
                                        <EditableField isEditing={isEditing}
                                            customClassName={profileFieldClassName}
                                            onChange={(update) => handleChange('province', update)}
                                            initialData={formData.province} /></th>
                                </tr>

                                <tr>
                                    <th className={styles.label}>Country</th>
                                    <th className={styles.control}>
                                        <EditableField
                                            isEditing={isEditing}
                                            customClassName={profileFieldClassName}
                                            onChange={(update) => handleChange('country', update)}
                                            initialData={formData.country} /></th>
                                </tr>

                                <tr>
                                    <th className={styles.label}> Postal Code </th>
                                    <th className={styles.control}>
                                        <EditableField isEditing={isEditing}
                                            customClassName={profileFieldClassName}
                                            onChange={(update) => handleChange('postalCode', update)}
                                            initialData={formData.postalCode} /></th>
                                </tr>

                                <tr className={styles.sectionHeaderRow}>
                                    <th className={styles.sectionHeader}>Payment Details</th>
                                    <th className={styles.sectionSpacer}></th>
                                </tr>

                                <tr>
                                    <th className={styles.sectionInstructionCell} colSpan={2}>
                                        <div className={styles.sectionInstruction}>
                                            Select a payment method.
                                        </div>
                                        <div className={styles.transferOptionGroup}>
                                            <label className={styles.radioOption}>
                                                <input
                                                    type="radio"
                                                    name="transferOption"
                                                    value="E_TRANSFER"
                                                    checked={selectedTransferOption === 'E_TRANSFER'}
                                                    onChange={() => handleTransferOptionChange('E_TRANSFER')}
                                                    disabled={!isEditing}
                                                />
                                                Interac e-transfer
                                            </label>
                                            <label className={styles.radioOption}>
                                                <input
                                                    type="radio"
                                                    name="transferOption"
                                                    value="BANK_TRANSFER"
                                                    checked={selectedTransferOption === 'BANK_TRANSFER'}
                                                    onChange={() => handleTransferOptionChange('BANK_TRANSFER')}
                                                    disabled={!isEditing}
                                                />
                                                Bank transfer
                                            </label>
                                        </div>
                                    </th>
                                </tr>

                                {isETransfer && (
                                <tr>
                                    <th className={styles.label}>
                                        <div className={styles.interacLabelWrap}>
                                            <div className={styles.interacInfoLabelRow}>
                                                <button
                                                    type="button"
                                                    className={styles.infoIcon}
                                                    title="Registered email or phone number"
                                                    data-tooltip="Registered email or phone number"
                                                    aria-label="Interac ID help: registered email or phone number"
                                                >
                                                    ?
                                                </button>
                                            </div>
                                            <span>Interac ID</span>
                                        </div>
                                    </th>
                                    <th className={styles.control}>
                                        <EditableField
                                            isEditing={isEditing}
                                            customClassName={profileFieldClassName}
                                            onChange={(update) => handleChange('interacId', update)}
                                            initialData={formData.interacId || ''}
                                        />
                                    </th>
                                </tr>
                                )}

                                {isETransfer && (
                                <tr>
                                    <th className={styles.label}>
                                        <div className={styles.interacLabelWrap}>
                                            <div className={styles.interacInfoLabelRow}>
                                                <button
                                                    type="button"
                                                    className={styles.infoIcon}
                                                    title="Must be alphanumeric and 3 to 25 characters"
                                                    data-tooltip="Our bank mandates to have one for e-transfer payments."
                                                    aria-label="Security Answer help: must be alphanumeric and 3 to 25 characters"
                                                >
                                                    ?
                                                </button>
                                            </div>
                                            <span>Security Answer</span>
                                        </div>
                                    </th>
                                    <th className={styles.control}>
                                        <EditableField
                                            isEditing={isEditing}
                                            customClassName={profileFieldClassName}
                                            onChange={(update) => handleChange('securityAnswer', update)}
                                            initialData={formData.securityAnswer || ''}
                                        />
                                    </th>
                                </tr>
                                )}

                                {isBankTransfer && (
                                <tr>
                                    <th className={styles.label}>Void Cheque</th>
                                    <th className={styles.control}>
                                        <div className={styles.voidChequeContainer}>
                                            {voidChequePreviewUrl && (
                                                <div className={styles.voidChequePreviewArea}>
                                                    {isPreviewImage && (
                                                        <img
                                                            src={voidChequePreviewUrl}
                                                            alt="Void cheque preview"
                                                            className={styles.voidChequePreviewImage}
                                                        />
                                                    )}
                                                    {isPreviewPdf && (
                                                        <embed
                                                            src={voidChequePreviewUrl}
                                                            type="application/pdf"
                                                            className={styles.voidChequePreviewPdf}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                            {isEditing && (
                                                <div className={styles.voidChequeActions}>
                                                    <input
                                                        ref={voidChequeInputRef}
                                                        type="file"
                                                        accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff,application/pdf,image/jpeg,image/png,image/tiff"
                                                        onChange={handleVoidChequeChange}
                                                        className={styles.hiddenFileInput}
                                                    />
                                                    <button
                                                        type="button"
                                                        className={styles.uploadFileButton}
                                                        onClick={handleUploadVoidChequeClick}
                                                    >
                                                        Upload
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={styles.removeFileButton}
                                                        onClick={handleRemoveVoidCheque}
                                                        disabled={!hasVoidCheque}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            )}
                                            <div className={styles.helperText}>Upload PDF, JPG, JPEG, PNG, or TIFF.</div>
                                        </div>
                                    </th>
                                </tr>
                                )}

                                {isEditing && hasChosenPaymentMethod && (
                                <tr>
                                    <th className={styles.label}></th>
                                    <th className={styles.control}>
                                        <label className={styles.confirmationRow}>
                                            <input
                                                type="checkbox"
                                                checked={isDetailsConfirmed}
                                                onChange={(event) => setIsDetailsConfirmed(event.target.checked)}
                                                className={styles.confirmationCheckbox}
                                            />
                                            I confirm these details are correct
                                        </label>
                                    </th>
                                </tr>
                                )}
                        </tbody>
                    </table>

                </div>
            </div>
        </div>
    );
};

export default MyProfile;
